/** @format */

import {LockClosedIcon} from '@heroicons/react/solid'
import isEqual from 'lodash.isequal'
import React, {useMemo} from 'react'

import ActionInput from '@src/components/tailwind/ActionInput'
import LoaderButton from '@src/components/tailwind/LoaderButton'
import {ContextModal} from '@src/components/tailwind/ModalContext'

import {useLoadingContext} from '@src/contexts'
import {useMultiSelectState} from '@src/hooks/useMultiSelectState'
import {usePendingField} from '@src/hooks/usePendingField'
import * as toast from '@src/toast'

import {getDisplayNameForFieldType} from '../FieldType'
import {
  FieldValidation,
  FieldValidationByType,
  isFieldValidationType,
} from '../FieldValidation'
import {useMimeTypeOptions} from '../useMimeTypeOptions'
import {useValidationApi} from '../ValidationApi'
import {Api} from '../ValidationRepository'
import {AddedByActionsText} from './AddedByActionsText'
import {EditFieldInput} from './EditFieldInput'
import {FieldNameInput} from './FieldNameInput'
import {RequiredSwitch} from './RequiredSwitch'

type EditFieldModalProps = {
  modal: string
  validation: FieldValidation
}

// EditFieldModal is the modal for updating validation rules for a FieldValidation.
export function EditFieldModal(props: EditFieldModalProps) {
  const {modal, validation} = props
  const displayTypeName = getDisplayNameForFieldType(validation.type.value)
  return (
    <ContextModal
      modal={modal}
      title={`${displayTypeName} validation settings`}
    >
      <ModalBody validation={validation} />
    </ContextModal>
  )
}

type ModalBodyProps = {
  validation: FieldValidation
}

function ModalBody(props: ModalBodyProps) {
  const {validation: v} = props

  if (isFieldValidationType('text', v)) {
    return <Text validation={v} />
  }

  if (isFieldValidationType('numeric', v)) {
    return <Numeric validation={v} />
  }

  if (isFieldValidationType('email', v) || isFieldValidationType('url', v)) {
    return <Basic validation={v} />
  }

  if (isFieldValidationType('datetime-local', v)) {
    return <DateTime validation={v} />
  }

  if (isFieldValidationType('file', v)) {
    return <File validation={v} />
  }

  const exhaustiveCheck: never = v
  throw new Error(`Unhandled validation type="${exhaustiveCheck}"`)
}

type EditFieldProps<K extends keyof FieldValidationByType> = {
  validation: FieldValidationByType[K]
}

function Text(props: EditFieldProps<'text'>) {
  const {validation: v} = props
  const maxLength = usePendingField(
    v.maxlength != null ? `${v.maxlength.value}` : '',
  )
  const saveUpdate = useSaveUpdate<'text'>(v.fieldName)

  return (
    <>
      <FieldNameInput validation={v} />
      <EditFieldInput
        type="number"
        label="Maximum Length"
        value={maxLength.value}
        addedBy={v.maxlength?.plugins}
        onChange={event => maxLength.setValue(event.target.value)}
        onSaveButtonClick={() =>
          saveUpdate({maxlength: parseNumber(maxLength.value)})
        }
        saveDisabled={!maxLength.isDirty}
      />
      <RequiredSwitch fieldName={v.fieldName} required={v.required} />
    </>
  )
}

function Numeric(props: EditFieldProps<'numeric'>) {
  const {validation: v} = props
  const min = usePendingField(v.min != null ? `${v.min.value}` : '')
  const max = usePendingField(v.max != null ? `${v.max.value}` : '')
  const saveUpdate = useSaveUpdate<'numeric'>(v.fieldName)

  return (
    <>
      <FieldNameInput validation={v} />
      <EditFieldInput
        type="number"
        label="Minimum Value"
        value={min.value}
        addedBy={v.min?.plugins}
        onChange={event => min.setValue(event.target.value)}
        onSaveButtonClick={() => saveUpdate({min: parseNumber(min.value)})}
        saveDisabled={!min.isDirty}
      />
      <EditFieldInput
        type="number"
        label="Maximum Value"
        value={max.value}
        addedBy={v.max?.plugins}
        onChange={event => max.setValue(event.target.value)}
        onSaveButtonClick={() => saveUpdate({max: parseNumber(max.value)})}
        saveDisabled={!max.isDirty}
      />
      <RequiredSwitch fieldName={v.fieldName} required={v.required} />
    </>
  )
}

function Basic(props: EditFieldProps<'email' | 'url'>) {
  const {validation: v} = props
  return (
    <>
      <FieldNameInput validation={v} />
      <RequiredSwitch fieldName={v.fieldName} required={v.required} />
    </>
  )
}

function DateTime(props: EditFieldProps<'datetime-local'>) {
  const {validation: v} = props
  const minDate = usePendingField(v.min?.value ?? '')
  const maxDate = usePendingField(v.max?.value ?? '')
  const saveUpdate = useSaveUpdate<'datetime-local'>(v.fieldName)

  return (
    <>
      <FieldNameInput validation={v} />
      <EditFieldInput
        type="datetime-local"
        label="Minimum Value"
        value={minDate.value}
        max={maxDate.value}
        addedBy={v.min?.plugins}
        onChange={event => minDate.setValue(event.target.value)}
        onSaveButtonClick={() =>
          saveUpdate({min: minDate.value !== '' ? minDate.value : undefined})
        }
        saveDisabled={!minDate.isDirty}
      />
      <EditFieldInput
        type="datetime-local"
        label="Maximum Value"
        value={maxDate.value}
        min={minDate.value}
        addedBy={v.max?.plugins}
        onChange={event => maxDate.setValue(event.target.value)}
        onSaveButtonClick={() =>
          saveUpdate({max: maxDate.value !== '' ? maxDate.value : undefined})
        }
        saveDisabled={!maxDate.isDirty}
      />
      <RequiredSwitch fieldName={v.fieldName} required={v.required} />
    </>
  )
}

function File(props: EditFieldProps<'file'>) {
  const {validation: v} = props
  const addedBy = v.accept?.plugins

  const mimeTypeOptions = useMimeTypeOptions()
  const originalAcceptSelection = useMemo(
    () => new Set(v.accept?.value),
    [v.accept?.value],
  )

  const accept = useMultiSelectState({
    initialSelectedKeys: v.accept?.value,
    items: mimeTypeOptions,
    getKey: option => option.value,
  })

  const saveUpdate = useSaveUpdate<'file'>(v.fieldName)

  const commonAcceptProps = {
    isMulti: true,
    label: 'Accept file types',
    options: mimeTypeOptions,
    value: [...accept.selectedKeys],
  }

  return (
    <>
      <FieldNameInput validation={v} />
      {addedBy ? (
        <ActionInput
          {...commonAcceptProps}
          readOnly
          description={
            <div className="flex items-center gap-1 text-xs text-gray-500">
              <LockClosedIcon className="h-4 w-4 shrink-0" />
              <AddedByActionsText addedBy={addedBy} />
            </div>
          }
        />
      ) : (
        <ActionInput
          {...commonAcceptProps}
          onChange={(selection: Array<{value: string}>) =>
            accept.setSelectedKeys(selection.map(s => s.value))
          }
          closeMenuOnSelect={false}
        >
          <LoaderButton
            // comparing sets so the order doesn't matter
            disabled={isEqual(accept.selectedKeys, originalAcceptSelection)}
            onClick={() =>
              saveUpdate({
                accept:
                  accept.selectedKeys.size > 0
                    ? [...accept.selectedKeys]
                    : undefined,
              })
            }
          >
            Save
          </LoaderButton>
        </ActionInput>
      )}
      <RequiredSwitch fieldName={v.fieldName} required={v.required} />
    </>
  )
}

function useSaveUpdate<T extends Api.UpdateValidationRulePayload['type']>(
  fieldName: string,
): (
  payload: Partial<Api.UpdateValidationRulePayloadByType[T]>,
) => Promise<void> {
  const {ready} = useLoadingContext()
  const api = useValidationApi()
  return async function update(payload) {
    try {
      await api.update(fieldName, payload)
    } catch (err) {
      toast.error(err)
    } finally {
      ready()
    }
  }
}

function parseNumber(s: string): number | undefined {
  return s !== '' && !Number.isNaN(+s) ? +s : undefined
}
